import { css } from "@emotion/react";

import SpaceAddButton from "./SpaceAddButton";
import SpaceItem from "./SpaceItem";
import { useNavigation } from "../../context/NavigationContext";

import { useApiGetSpaceList } from "@/hooks/api/space/useApiGetSpaceList";
import { PROJECT_CATEGORY_MAP } from "../../constants";
import { useEffect, useRef } from "react";
import { LoadingSpinner } from "@/component/space/view/LoadingSpinner";
import AddSpacePage from "@/app/desktop/space/add/AddSpacePage";
import useDesktopBasicModal from "@/hooks/useDesktopBasicModal";
import { useRetrospectCreateReset } from "@/hooks/store/useRetrospectCreateReset";
import { useSpaceCreateReset } from "@/hooks/store/useSpaceCreateReset";
import { Tooltip } from "@/component/common/tip/Tooltip";
import { useApiPostSpacesImpression } from "@/hooks/api/backoffice/useApiPostSpacesImpression";
import { trackEvent } from "@/lib/google-analytics";
import { GA_EVENTS } from "@/lib/google-analytics/events";

interface SpacesListProps {
  currentTab: "전체" | "개인" | "팀";
}

// 온보딩 툴팁과 "스페이스 추가" 버튼 사이 세로 간격
// LNB가 접히면 버튼이 작아져 더 위로 붙인다.
const TOOLTIP_OFFSET_Y = {
  collapsed: -6,
  expanded: 14,
} as const;

export default function SpacesList({ currentTab }: SpacesListProps) {
  const { isCollapsed } = useNavigation();

  const currentCategory = PROJECT_CATEGORY_MAP[currentTab];

  const observerRef = useRef<HTMLDivElement>(null);

  const { data: spaceData, hasNextPage, isPending, isFetchingNextPage, fetchNextPage } = useApiGetSpaceList(currentCategory);

  const spaces = spaceData?.pages.flatMap((page) => page.data) ?? [];

  const isEmptySpace = spaces.length === 0;

  const { open: openDesktopModal } = useDesktopBasicModal();
  const { resetAll: resetRetrospectInfo } = useRetrospectCreateReset();
  const { resetAll: resetSpaceInfo } = useSpaceCreateReset();
  const { mutate: postSpacesImpression } = useApiPostSpacesImpression();

  const handleOpenSpaceAdd = () => {
    openDesktopModal({
      title: "",
      contents: <AddSpacePage />,
      options: {
        enableFooter: false,
      },
      onClose: () => {
        resetRetrospectInfo();
        resetSpaceInfo();
      },
    });

    trackEvent(GA_EVENTS.SPACE.ADD_BUTTON);
  };

  useEffect(() => {
    const element = observerRef.current;

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];

      if (target.isIntersecting && hasNextPage) {
        fetchNextPage();
      }
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
    });

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [hasNextPage, fetchNextPage]);

  useEffect(() => {
    postSpacesImpression();
  }, []);

  if (isPending && !isFetchingNextPage) {
    return <LoadingSpinner />;
  }

  return (
    <ul
      css={css`
        display: flex;
        flex-direction: column;
        align-items: ${isCollapsed ? "center" : "flex-start"};
        gap: 0.4rem;
        margin-top: 1.2rem;
        padding: 0;
        flex: 1;
        overflow-y: auto;
      `}
    >
      {spaces.map((space) => (
        <SpaceItem key={space.id} space={space} />
      ))}

      <section
        css={css`
          width: 100%;
        `}
      >
        {isEmptySpace ? (
          <Tooltip>
            <Tooltip.Trigger>
              <SpaceAddButton onClick={handleOpenSpaceAdd} />
            </Tooltip.Trigger>
            <Tooltip.Content
              message="스페이스를 생성해야 회고를 진행할 수 있어요!"
              placement="bottom-start"
              offsetY={isCollapsed ? TOOLTIP_OFFSET_Y.collapsed : TOOLTIP_OFFSET_Y.expanded}
              arrowOffsetX={2}
              autoHide={false}
              hideOnClick={false}
            />
          </Tooltip>
        ) : (
          <SpaceAddButton onClick={handleOpenSpaceAdd} />
        )}
      </section>

      {hasNextPage && <div ref={observerRef} style={{ height: "1px" }} />}

      {isFetchingNextPage && <LoadingSpinner />}
    </ul>
  );
}
